import { TermType } from '../constants/termTypes';
import { getCompatiblePriceModels, getListPriceModels } from './ProductPrice';

/**
 * Checks whether the new skus can be added to the term provided.
 * @param term        The term object.
 * @param termItems   The term item dictionary json object. e.g. { "CIM-123" => {}, "CIM-345" => {}, ... }
 * @param newSkus     The array of new skus.
 * @return Return object that will always contain `allowed` boolean field and an optional `problem` in case that the update is not allowed.
 */
export const canSkusBeAddedToTerm = async (term, termItems, newSkus) => {
  const { termId, revisionId, type, seller } = term;
  if (!termId || !revisionId || !type || !seller || !termItems || !newSkus || !newSkus.length) {
    throw new Error('The required field(s) or parameter(s) are missing.');
  }

  const currentSkus = Object.keys(termItems);
  const allSkusSet = new Set(currentSkus);
  newSkus.forEach(newSku => allSkusSet.add(newSku));
  const allSkusArray = Array.from(allSkusSet);

  if (type === TermType.LIST_PRICE) {
    // list price terms can be updated if all the list prices are set up for all skus.
    const listPriceModels = await getListPriceModels(seller, allSkusArray);

    const skusWithListPrices = new Set(
      listPriceModels.map(lp => lp.skus || []).reduce((a, b) => {
        return a.concat(b);
      }, [])
    );

    const skusWithoutListPrice = allSkusArray.filter(sku => !skusWithListPrices.has(sku));

    if (skusWithoutListPrice.length > 0) {
      return {
        allowed: false,
        problem: {
          message: 'Not all the skus have catalog prices set up.',
          details: skusWithoutListPrice
        }
      };
    }

    return {
      allowed: true
    };
  } else if (type === TermType.SPECIAL_PRICE) {
    // special price terms can be updated if..
    // 1. All the current items have the same price models AND
    // 2. All the current+new skus have compatible price models AND
    // 3. The current price model is one of the compatible price models.
    const currentPriceModel = termItems[currentSkus[0]].priceModel;
    const currentItemsHaveSamePriceModel = Object.values(termItems).every(
      item => item && item.priceModel && item.priceModel.id === currentPriceModel.id
    );

    if (!currentItemsHaveSamePriceModel) {
      return {
        allowed: false,
        problem: {
          message: 'Items do not have the same price models'
        }
      };
    }

    const compatiblePriceModels = await getCompatiblePriceModels(seller, allSkusArray);

    if (compatiblePriceModels.length === 0) {
      return {
        allowed: false,
        problem: {
          message: 'There are no compatible price models.'
        }
      };
    }

    const isCurrentPriceModelCompatible = compatiblePriceModels.some(
      ({ id, revisionId }) => currentPriceModel.id === id && currentPriceModel.revisionId === revisionId
    );

    if (!isCurrentPriceModelCompatible) {
      return {
        allowed: false,
        problem: {
          message: 'Current price model is not compatible with the skus.',
          details: compatiblePriceModels
        }
      };
    }

    // no issues with special price, so return true.
    return {
      allowed: true
    };
  }

  // since the term is neither list or special price, return false.
  return {
    allowed: false
  };
};
